import React, { useMemo, useState, useEffect } from "react";
import { Button, Spinner, Tab, Row, Nav, ToggleButton } from "react-bootstrap";
import { useSelector, shallowEqual } from "react-redux";
import cogoToast from "cogo-toast";
import { 
    useGetAddressUpdateRequestsQuery, 
    useGetDocumentQuery, 
    useApproveAddressUpdateMutation, 
    useRejectAddressUpdateMutation 
} from "../../../../../../../redux/apiSlice";
import MagnifiableImage from "../../../../../Common/components/MagnifiableImage";
import { Form as FormLayout, Col } from "react-bootstrap";
import { formatStatus, formatDate, formatUsername } from "../../../../../../util/Helpers";
import { useMultiFormContext } from "../../../../../Common/components/MultiForm";
import { defaultRejectReason } from "../../../../data/DefaultRejectReason";

export function AddressUpdateInformation({ initialValues: user, dataKey }) {
    // Fetch all address updates for this user
    const { data: addressUpdates, isLoading, refetch } = useGetAddressUpdateRequestsQuery({ 
        userId: user.userId
    });

    const updates = addressUpdates?.data || addressUpdates?.rows || addressUpdates || [];

    const hasPending = useMemo(() => {
        return updates.some(u => u.status === 'PENDING');
    }, [updates]);

    const { upsertValue } = useMultiFormContext();

    useEffect(() => {
        upsertValue(dataKey, {
            isValid: !hasPending,
            isDirty: false
        });
    }, [hasPending, upsertValue, dataKey]);

    if (isLoading) {
        return <div className="d-flex justify-content-center align-items-center h-100"><Spinner animation="border" /></div>;
    }

    if (!updates || updates.length === 0) {
        return <div className="d-flex justify-content-center align-items-center h-100"><h3>No address updates found</h3></div>;
    }


    // Reuse the layout from Documents tab
    return (
        <Tab.Container id="address-update-tabs" defaultActiveKey={updates[0].updateAddressId} className={"h-100"}>
            <Row className={"h-100"}>
                <Col
                    sm={4}
                    className={"d-flex flex-column border-right h-100 overflow-auto"}
                >
                    <h3 className="font-weight-bolder">Address Update</h3>
                    <Nav className="flex-column">
                        {updates.map((update) => (
                            <Nav.Item key={update.updateAddressId} className={"w-100"}>
                                <Nav.Link
                                    eventKey={update.updateAddressId}
                                    className={`px-0 border-top`}
                                >
                                    <div className={"px-3 py-2 d-flex align-items-center"}>
                                        <div className={"flex-fill"}>Address Update</div>
                                        {formatStatus(update.status)}
                                    </div>
                                </Nav.Link>
                            </Nav.Item>
                        ))}
                    </Nav>
                </Col>
                <Col sm={8} className={"h-100 overflow-auto"}>
                    <Tab.Content className={"h-100"}>
                        {updates.map((update) => (
                            <Tab.Pane key={update.updateAddressId} eventKey={update.updateAddressId} className={"h-100"}>
                                <AddressUpdateDetail 
                                    addressUpdate={update} 
                                    user={user} 
                                    refetch={refetch}
                                />
                            </Tab.Pane>
                        ))}
                    </Tab.Content>
                </Col>
            </Row>
        </Tab.Container>
    );
}

function AddressUpdateDetail({ addressUpdate, user, refetch }) {
    const adminUser = useSelector(({ auth }) => (auth.user), shallowEqual);
    const docParams = useMemo(() => ({
        userGroup: user.userGroup,
        userId: user.userId,
        documentId: addressUpdate.documentId
    }), [user, addressUpdate]);

    const { data: documentData, isLoading: isDocLoading } = useGetDocumentQuery(docParams, { skip: !addressUpdate.documentId });

    const [approveAddressUpdate, { isLoading: isApproving }] = useApproveAddressUpdateMutation();
    const [rejectAddressUpdate, { isLoading: isRejecting }] = useRejectAddressUpdateMutation();

    const [rejectionReason, setRejectionReason] = useState("");
    const [showRejectionInput, setShowRejectionInput] = useState(false);

    const handleApprove = async () => {
        try {
            await approveAddressUpdate({
                updateAddressId: addressUpdate.updateAddressId,
                reviewedBy: adminUser?.email
            }).unwrap();
            cogoToast.success("Address update approved");
            refetch();
        } catch (err) {
            cogoToast.error(err?.data?.message || err?.message || "Error approving address update");
        }
    };

    const handleReject = async () => {
        if (!showRejectionInput) {
            setShowRejectionInput(true);
            return;
        }
        if (!rejectionReason.trim()) {
            cogoToast.warn("Please enter a rejection reason");
            return;
        }
        try {
            await rejectAddressUpdate({
                updateAddressId: addressUpdate.updateAddressId,
                reviewedBy: adminUser?.email,
                rejectionReason
            }).unwrap();
            cogoToast.success("Address update rejected");
            refetch();
        } catch (err) {
            cogoToast.error(err?.data?.message || err?.message || "Error rejecting address update");
        }
    };

    const a = addressUpdate.addressData || {};

    return (
        <div className="d-flex flex-column h-100">
            <div className="rounded-lg flex-fill bg-light-secondary mb-4">
                {isDocLoading ? (
                    <div className="d-flex justify-content-center align-items-center h-100"><Spinner animation="border" /></div>
                ) : (
                    <MagnifiableImage
                        height={"100%"}
                        width={"100%"}
                        src={documentData?.document}
                        alt={documentData?.type ?? "Document"}
                    />
                )}
            </div>

            <div className="pt-4 border-top">
                <fieldset className="d-flex flex-column">
                    <div className="mb-4">

                        <div className="mb-3">
                            <h6>Street</h6>
                            <span className="text-muted font-weight-bolder font-size-lg">{addressUpdate?.addressData?.street1}</span>
                        </div>

                        <div className="row mb-3">
                            <div className="col-6">
                                <h6>State</h6>
                                <span className="text-muted">{addressUpdate?.addressData?.city}</span>
                            </div>
                            <div className="col-6">
                                <h6>Province</h6>
                                <span className="text-muted">{addressUpdate?.addressData?.province}</span>
                            </div>
                        </div>

                        <div className="mb-3">
                            <h6>Postal Code</h6>
                            <span className="text-muted">{addressUpdate?.addressData?.postalCode || "None"}</span>
                        </div>

                        <div className="row mb-3">
                            <div className="col-6">
                                <h6>Created</h6>
                                <span className="text-muted">{formatDate(addressUpdate?.created)}</span>
                            </div>
                            <div className="col-6">
                                <h6>Updated</h6>
                                <span className="text-muted">{formatDate(addressUpdate?.updated)}</span>
                            </div>
                        </div>
                    </div>

                    <div>
                        {showRejectionInput ? (
                            <div className="row mb-4 mt-2">
                                <div className="col-12">
                                    <label>Rejection Reason</label>
                                    <input
                                        className="form-control border-danger"
                                        type="text"
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        placeholder="Enter reason for rejection..."
                                        list="rejection-reasons-list"
                                        name="rejectionReason"
                                    />
                                    <datalist id="rejection-reasons-list">
                                        {defaultRejectReason.map(({ id, text }) => (
                                            <option key={id} value={text} />
                                        ))}
                                    </datalist>
                                </div>
                                <div className="col-12 mt-2">
                                    <div className="d-flex flex-wrap">
                                        {defaultRejectReason.map(({ id, text }) => (
                                            <ToggleButton
                                                key={id}
                                                id={`rejectionReason-${id}`}
                                                type="radio"
                                                name="rejectionReason"
                                                value={text}
                                                variant={`outline-${rejectionReason === text ? "danger" : "secondary"}`}
                                                className="mr-2 mb-2"
                                                checked={rejectionReason === text}
                                                onChange={(e) => setRejectionReason(e.currentTarget.value)}
                                            >
                                                {text}
                                            </ToggleButton>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <FormLayout.Row>
                        {showRejectionInput ? (
                            <>
                                <Col>
                                    <Button 
                                        variant="secondary" 
                                        className="w-100"
                                        onClick={() => {
                                            setShowRejectionInput(false);
                                            setRejectionReason("");
                                        }}
                                        disabled={isApproving || isRejecting || addressUpdate.status === "REJECTED"}
                                    >
                                        Cancel
                                    </Button>
                                </Col>
                                <Col>
                                    <Button 
                                        variant="danger" 
                                        className="w-100" 
                                        onClick={handleReject} 
                                        disabled={isApproving || isRejecting || addressUpdate.status === "REJECTED"}
                                    >
                                        {isRejecting ? "Rejecting..." : "Reject"}
                                    </Button>
                                </Col>
                            </>
                        ) : (
                            <>
                                <Col>
                                    <Button 
                                        variant="outline-danger" 
                                        className="w-100"
                                        onClick={() => setShowRejectionInput(true)} 
                                        disabled={isApproving || isRejecting || addressUpdate.status === "REJECTED"}
                                    >
                                        Reject
                                    </Button>
                                </Col>
                                <Col>
                                    <Button 
                                        variant="dark" 
                                        className="w-100" 
                                        onClick={handleApprove} 
                                        disabled={isApproving || isRejecting || addressUpdate.status === "REJECTED"}
                                    >
                                        {isApproving ? "Approving..." : "Approve"}
                                    </Button>
                                </Col>
                            </>
                        )}
                    </FormLayout.Row>
                </fieldset>
            </div>
        </div>
    );
}
